import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface StatProps {
  value: string;
  label: string;
  delay: number;
}

function AnimatedStat({ value, label, delay }: StatProps) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`text-center transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <p className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-gold mb-2 tracking-tight">
        {value}
      </p>
      <p className="text-sm md:text-base text-charcoal-200 font-sans leading-relaxed max-w-[200px] mx-auto">
        {label}
      </p>
    </div>
  );
}

export default function IowaWaterSection() {
  const [reveal, setReveal] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setReveal(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: "2x", label: "The increased risk of certain cancers, such as ovarian cancer, observed in Iowa women exposed to elevated nitrate levels in public drinking water.", delay: 100 },
    { value: "2nd", label: "Iowa's national rank for overall cancer incidence, prompting ongoing public health research.", delay: 250 },
    { value: "10 mg/L", label: "The EPA's current legal limit for nitrates—a standard originally set in 1962.", delay: 400 },
    { value: "$1.5M", label: "The historical annual cost for Des Moines Water Works to strip nitrates from the municipal supply.", delay: 550 },
  ];

  return (
    <section ref={sectionRef} className="bg-dark-bg py-24 md:py-32">
      <div className="w-full px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div
          className={`max-w-3xl mb-16 md:mb-20 transition-all duration-700 ${
            reveal ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-xs md:text-sm font-sans font-semibold tracking-[0.2em] text-gold uppercase mb-4">
            The Iowa Water Crisis
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-medium text-charcoal-50 leading-tight tracking-tight-xl mb-6">
            Iowa's water supply faces complex challenges.
            <br />
            <span className="text-gold">Here is what the data shows.</span>
          </h2>
          <p className="text-base md:text-lg text-charcoal-300 font-sans leading-relaxed max-w-2xl">
            Decades of agricultural runoff have impacted Iowa's groundwater, making water quality a top priority for families. Researchers are actively studying the correlation between agricultural states, heavy nitrate loads, and public health.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-16 md:mb-20">
          {stats.map((stat) => (
            <AnimatedStat
              key={stat.label}
              value={stat.value}
              label={stat.label}
              delay={stat.delay}
            />
          ))}
        </div>

        {/* Read More Link */}
        <div
          className={`transition-all duration-700 delay-300 ${
            reveal ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <Link
            to="/iowa-water-crisis"
            className="inline-flex items-center gap-2 text-sm font-sans font-semibold text-gold hover:text-gold-light transition-colors duration-300"
          >
            Read the full research
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
        {/* Disclaimer */}
        <div
          className={`mt-16 md:mt-20 p-5 md:p-6 rounded-xl border border-gold/20 bg-dark-surface/50 transition-all duration-700 delay-500 ${
            reveal ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-xs md:text-sm text-charcoal-400 font-sans leading-relaxed">
            Disclaimer: Our filtration systems are designed to reduce specific contaminants from drinking water. They are not intended to diagnose, treat, cure, or prevent any disease.
          </p>
        </div>
      </div>
    </section>
  );
}